import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Programs from "./pages/Programs";
import CreateProgram from "./pages/CreateProgram";
import ProgramDetails from "./pages/ProgramDetails";
import Merchants from "./pages/Merchants";
import CreateMerchant from "./pages/CreateMerchant";
import MerchantDetails from "./pages/MerchantDetails";
import Transactions from "./pages/Transactions";
import Invoices from "./pages/Invoices";
import Settings from "./pages/Settings";
import Jobs from "./pages/Jobs";
import Users from "./pages/Users";
import Analytics from "./pages/Analytics";
import { DashboardLayout } from "./components/layout/dashboard-layout";
import PerkPointHome from "./pages/perkpoint/PerkPointHome";
import PerkPointProgramDetails from "./pages/perkpoint/ProgramDetails";
import PerkPointProgramSetup from "./pages/perkpoint/ProgramSetup";
import PerkPointAllMembers from "./pages/perkpoint/AllMembers";
import PerkPointMemberHistory from "./pages/perkpoint/MemberHistory";
import PerkPointMemberSegments from "./pages/perkpoint/MemberSegments";
import PerkPointMemberInsight from "./pages/perkpoint/MemberInsight";
import RewardsRules from "./pages/perkpoint/rewards/Rules";
import RewardsTiers from "./pages/perkpoint/rewards/Tiers";
import RewardsCampaigns from "./pages/perkpoint/rewards/Campaigns";
import CampaignDetails from "./pages/perkpoint/rewards/CampaignDetails";
import CampaignBuilder from "./pages/perkpoint/rewards/campaign-builder/CampaignBuilder";
import CampaignPerformance from "./pages/perkpoint/rewards/campaign-performance/CampaignPerformance";
import RewardsCoupons from "./pages/perkpoint/rewards/Coupons";
import RewardsGamification from "./pages/perkpoint/rewards/Gamification";
import AuditHistory from "./pages/perkpoint/audit/AuditHistory";
import PerkPointCreateProgram from "./pages/perkpoint/CreateProgram";
import PerkPointCreateMember from "./pages/perkpoint/CreateMember";
import PerkPointMemberDetails from "./pages/perkpoint/MemberDetails";
import PerkPointMembers from "./pages/perkpoint/Members";
import PerkPointTransactions from "./pages/perkpoint/Transactions";
import PerkPointAnalytics from "./pages/perkpoint/Analytics";
import RuleEngine from "./pages/perkpoint/RuleEngine";
import Coupons from "./pages/perkpoint/Coupons";
import Tier from "./pages/perkpoint/Tier";
import Campaign from "./pages/perkpoint/Campaign";
import Segmentation from "./pages/perkpoint/Segmentation";
import Outlets from "./pages/perkpoint/Outlets";
import Gamification from "./pages/perkpoint/Gamification";
import GamePointGamification from "./pages/gamepoint/GamePointGamification";
import Report from "./pages/perkpoint/Report";
import PublicQuiz from "./pages/perkpoint/gamification/PublicQuiz";
import Communication from "./pages/perkpoint/Communication";
import Member360 from "./pages/perkpoint/Member360";
import UserRolesManagement from "./pages/perkpoint/UserRolesManagement";
import AIAssistance from "./pages/perkpoint/AIAssistance";
import AIChat from "./pages/perkpoint/AIChat";
import FraudDetection from "./pages/perkpoint/FraudDetection";
import AIRecommendations from "./pages/perkpoint/AIRecommendations";
import InsertTransaction from "./pages/perkpoint/transaction/InsertTransaction";
import ReportsInsights from "./pages/ReportsInsights";
import NotFound from "./pages/NotFound";
import { AuthProvider } from "./contexts/AuthContext";
import AccountingEntry from "./pages/AccountingEntry";
import RewardPointLayout from "./pages/rewardpoint";
import ProductsLayout from "./layouts/rewardpoint/ProductsLayout";
import RewardPointPrograms from "./pages/rewardpoint/programs";
import RewardPointBrands from "./pages/rewardpoint/brands";
import RewardPointOrders from "./pages/rewardpoint/orders";
import RewardPointInvoices from "./pages/rewardpoint/invoices";
import CreateRewardPointProgram from "./pages/rewardpoint/programs/create";
import RedemptionMerchants from "./pages/rewardpoint/redemption-merchants";
import RewardProgramDetails from "./pages/rewardpoint/RewardProgramDetails";
import XpandPointDashboard from "./pages/xpandpoint/Dashboard";
import XpandPointPrograms from "./pages/xpandpoint/TransferPrograms";
import XpandPointTransactions from "./pages/xpandpoint/Transactions";
import XpandPointAccountingEntry from "./pages/xpandpoint/AccountingEntry";
import XpandPointInvoices from "./pages/xpandpoint/Invoices";
import TransferPrograms from "./pages/xpandpoint/TransferPartners";
import TransferPartnerDetails from "./pages/xpandpoint/TransferPartnerDetails";
import TransferPartnerCreate from "./pages/xpandpoint/TransferPartnerCreate";
import XpandPointCreateProgram from "./pages/xpandpoint/TransferProgramCreate";
import XpandPointProgramDetails from "./pages/xpandpoint/TransferProgramDetails";
import PlatformCategories from "./pages/rewardpoint/PlatformCategories";
import ProgramCategories from "./pages/rewardpoint/program-categories";
import ShippingProfiles from "./pages/rewardpoint/ShippingProfiles";
import CreateShippingProfile from "./pages/rewardpoint/CreateShippingProfile";
import ShippingProfileDetails from "./pages/rewardpoint/ShippingProfileDetails";
import OrderDetails from "./pages/rewardpoint/orders/OrderDetails";
import ProductsPage from "./pages/rewardpoint/products";
import MerchandisePage from "./pages/rewardpoint/products/merchandise";
import FlightBookingPage from "./pages/rewardpoint/products/flight-booking";
import FlightBookingProductCreatePage from "./pages/rewardpoint/products/flight-booking-create";
import HotelBookingPage from "./pages/rewardpoint/products/hotel-booking";
import GiftCardProductCreatePage from "./pages/rewardpoint/products/gift-card-create";
import { PointerEventsFix } from "./components/PointerEventsFix";
import RedemptionMerchantView from "./pages/rewardpoint/redemption-merchants/view";
import ForgotPassword from "./pages/ForgotPassword";
import PasswordChangedSuccess from "./pages/PasswordChangedSuccess";
import MerchandiseProductCreatePage from "./pages/rewardpoint/products/merchandise-create";
import HotelBookingProductCreatePage from "./pages/rewardpoint/products/hotel-booking-create";
import GiftCardProductView from "./pages/rewardpoint/products/gift-card-view";
import MerchandiseProductView from "./pages/rewardpoint/products/merchandise-view";
import FlightBookingProductView from "./pages/rewardpoint/products/flight-booking-view";
import HotelBookingProductView from "./pages/rewardpoint/products/hotel-booking-view";
import Clients from "./pages/Clients";
import CreateClient from "./pages/CreateClient";
import ClientDetails from "./pages/ClientDetails";
import RedemptionMerchantCreate from "./pages/rewardpoint/redemption-merchants/create";
import GiftCardDashboard from "./pages/giftcard/GiftCardDashboard";
import AllGiftCards from "./pages/giftcard/AllGiftCards";
import IssueGiftCard from "./pages/giftcard/CreateGiftCard";
import RedeemGiftCard from "./pages/giftcard/RedeemGiftCard";
import GiftCardLogs from "./pages/giftcard/Logs";
import CreateGiftCardList from "./pages/giftcard/CreateGiftCardList";
import CreateNewGiftCard from "./pages/giftcard/CreateNewGiftCard";
import GiftCardBrands from "./pages/giftcard/Brands";
import CreateGiftCardBrand from "./pages/giftcard/CreateBrand";
import GiftCardMall from "./pages/giftcard/Mall";
import GiftCardSettings from "./pages/giftcard/Settings";
import GiftCardCampaign from "./pages/giftcard/GiftCardCampaign";
import { OffersLayout } from "./pages/offers/OffersLayout";
import OffersDashboard from "./pages/offers/OffersDashboard";
import OffersCreateProgram from "./pages/offers/CreateProgram";
import OffersCreateMerchant from "./pages/offers/CreateMerchant";
import OfferProgramDetails from "./pages/offers/OfferProgramDetails";
import OfferMerchantDetails from "./pages/offers/OfferMerchantDetails";
import OffersRecommendation from "./pages/offers/Recommendation";
import VerifyMfa from "./pages/VerifyMfa";
import VerifyEmail from "./pages/VerifyEmail";
import PublicSpinWheel from "./pages/perkpoint/gamification/PublicSpinWheel";
import PublicScratchCard from "./pages/perkpoint/gamification/PublicScratchCard";
import PublicSlotMachine from "./pages/gamepoint/slotmachine/PublicSlotMachine";
import PublicMysteryBox from "./pages/gamepoint/mysterybox/PublicMysteryBox";
import PublicOffers from "./pages/offers/PublicOffers";
import OfferStorefront from "./pages/offers/storefront/OfferStorefront";
import MemberDetailsPage from "./pages/member-details/MemberDetailsPage";
import { DocumentTitle } from "./components/DocumentTitle";
import { MemberAuthProvider } from "./pages/member-portal-v2/auth/MemberAuthContext";
import { MemberLayout } from "./pages/member-portal-v2/components/MemberLayout";
import { MemberRequireAuth } from "./pages/member-portal-v2/components/MemberRequireAuth";
import MemberLogin from "./pages/member-portal-v2/MemberLogin";
import MemberHome from "./pages/member-portal-v2/MemberHome";
import MemberHotels from "./pages/member-portal-v2/MemberHotels";
import MemberHotelDetails from "./pages/member-portal-v2/MemberHotelDetails";
import MemberBooking from "./pages/member-portal-v2/MemberBooking";
import MemberFlights from "./pages/member-portal-v2/MemberFlights";
import MemberFlightBook from "./pages/member-portal-v2/MemberFlightBook";
import MemberTrips from "./pages/member-portal-v2/MemberTrips";
import MemberRewards from "./pages/member-portal-v2/MemberRewards";
import MemberTransactions from "./pages/member-portal-v2/MemberTransactions";
import MemberProfile from "./pages/member-portal-v2/MemberProfile";

const queryClient = new QueryClient();

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <div>
            <PointerEventsFix />
            <Toaster />
            <SonnerToaster />
            <BrowserRouter>
              <DocumentTitle />
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* <Route path="/reset-password" element={<ResetPassword />} /> */}
                <Route
                  path="/password-changed-success"
                  element={<PasswordChangedSuccess />}
                />
                <Route path="/auth/verify-mfa" element={<VerifyMfa />} />
                <Route path="/auth/verify" element={<VerifyEmail />} />
                <Route path="/spin-wheel" element={<PublicSpinWheel />} />
                <Route path="/scratch-card" element={<PublicScratchCard />} />
                <Route path="/slot-machine" element={<PublicSlotMachine />} />
                <Route path="/mystery-box" element={<PublicMysteryBox />} />
                <Route path="/publicoffers" element={<PublicOffers />} />
                <Route
                  path="/offers/storefront"
                  element={<OfferStorefront />}
                />
                <Route path="/member-details" element={<MemberDetailsPage />} />
                <Route path="/quiz" element={<PublicQuiz />} />

                {/* Member Portal V2 — hotel marketplace, independent of admin auth */}
                <Route
                  path="/member"
                  element={
                    <MemberAuthProvider>
                      <Outlet />
                    </MemberAuthProvider>
                  }
                >
                  <Route path="login" element={<MemberLogin />} />
                  <Route element={<MemberRequireAuth />}>
                    <Route element={<MemberLayout />}>
                      <Route index element={<MemberHome />} />
                      <Route path="hotels" element={<MemberHotels />} />
                      <Route
                        path="hotels/:hotelId"
                        element={<MemberHotelDetails />}
                      />
                      <Route
                        path="booking/:hotelId"
                        element={<MemberBooking />}
                      />
                      <Route path="flights" element={<MemberFlights />} />
                      <Route
                        path="flights/:flightId"
                        element={<MemberFlightBook />}
                      />
                      <Route path="trips" element={<MemberTrips />} />
                      <Route
                        path="bookings"
                        element={<Navigate to="/member/trips" replace />}
                      />
                      <Route path="rewards" element={<MemberRewards />} />
                      <Route path="transactions" element={<MemberTransactions />} />
                      <Route
                        path="offers"
                        element={<Navigate to="/member/rewards" replace />}
                      />
                      <Route path="profile" element={<MemberProfile />} />
                      <Route
                        path="dashboard"
                        element={<Navigate to="/member" replace />}
                      />
                      <Route
                        path="marketplace"
                        element={<Navigate to="/member" replace />}
                      />
                      <Route
                        path="marketplace/hotels/:hotelId"
                        element={<Navigate to="/member/hotels" replace />}
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Protected Routes with unified layout */}
                <Route
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  {/* Access Point Routes */}
                  <Route path="/accesspoint" element={<Dashboard />} />
                  <Route path="/accesspoint/programs" element={<Programs />} />
                  <Route
                    path="/accesspoint/programs/create"
                    element={<CreateProgram />}
                  />
                  <Route
                    path="/accesspoint/programs/:id"
                    element={<ProgramDetails />}
                  />
                  <Route
                    path="/accesspoint/merchants"
                    element={<Merchants />}
                  />
                  <Route
                    path="/accesspoint/merchants/create"
                    element={<CreateMerchant />}
                  />
                  <Route
                    path="/accesspoint/merchants/:id"
                    element={<MerchantDetails />}
                  />
                  <Route
                    path="/accesspoint/transactions"
                    element={<Transactions />}
                  />
                  <Route
                    path="/accesspoint/accounting-entry"
                    element={<AccountingEntry />}
                  />
                  <Route path="/accesspoint/invoices" element={<Invoices />} />
                  <Route
                    path="/accesspoint/analytics"
                    element={<Analytics />}
                  />
                  <Route
                    path="/accesspoint/reports-insights"
                    element={<ReportsInsights />}
                  />
                  <Route path="/accesspoint/jobs" element={<Jobs />} />
                  <Route path="/accesspoint/clients" element={<Clients />} />
                  <Route
                    path="/accesspoint/clients/create"
                    element={<CreateClient />}
                  />
                  <Route
                    path="/accesspoint/clients/:id"
                    element={<ClientDetails />}
                  />

                  {/* Offer Point — same program/merchant flows as Access Point; themed shell */}
                  <Route path="/offers" element={<OffersLayout />}>
                    <Route index element={<OffersDashboard />} />
                    <Route
                      path="programs"
                      element={<Programs basePath="/offers" />}
                    />
                    <Route
                      path="programs/create"
                      element={<OffersCreateProgram />}
                    />
                    <Route
                      path="programs/:id"
                      element={<OfferProgramDetails />}
                    />
                    <Route
                      path="merchants"
                      element={<Merchants basePath="/offers" />}
                    />
                    <Route
                      path="merchants/create"
                      element={<OffersCreateMerchant />}
                    />
                    <Route
                      path="merchants/:id"
                      element={<OfferMerchantDetails />}
                    />
                    <Route
                      path="recommendation"
                      element={<OffersRecommendation />}
                    />
                  </Route>

                  {/* Legacy root routes - redirect to accesspoint paths */}
                  <Route
                    path="/"
                    element={<Navigate to="/accesspoint" replace />}
                  />
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/accesspoint" replace />}
                  />
                  <Route
                    path="/programs"
                    element={<Navigate to="/accesspoint/programs" replace />}
                  />
                  <Route
                    path="/programs/create"
                    element={
                      <Navigate to="/accesspoint/programs/create" replace />
                    }
                  />
                  <Route
                    path="/programs/:id"
                    element={
                      <Navigate to="/accesspoint/programs/:id" replace />
                    }
                  />
                  <Route
                    path="/merchants"
                    element={<Navigate to="/accesspoint/merchants" replace />}
                  />
                  <Route
                    path="/merchants/create"
                    element={
                      <Navigate to="/accesspoint/merchants/create" replace />
                    }
                  />
                  <Route path="/merchants/:id" element={<MerchantDetails />} />
                  <Route
                    path="/transactions"
                    element={
                      <Navigate to="/accesspoint/transactions" replace />
                    }
                  />
                  <Route
                    path="/invoices"
                    element={<Navigate to="/accesspoint/invoices" replace />}
                  />
                  <Route
                    path="/analytics"
                    element={<Navigate to="/accesspoint/analytics" replace />}
                  />
                  <Route
                    path="/jobs"
                    element={<Navigate to="/accesspoint/jobs" replace />}
                  />
                  <Route
                    path="/clients"
                    element={<Navigate to="/accesspoint/clients" replace />}
                  />
                  <Route
                    path="/clients/create"
                    element={
                      <Navigate to="/accesspoint/clients/create" replace />
                    }
                  />
                  <Route
                    path="/clients/:id"
                    element={<Navigate to="/accesspoint/clients/:id" replace />}
                  />

                  {/* Perk Point Routes */}
                  <Route path="/perkpoint" element={<PerkPointHome />} />
                  <Route
                    path="/perkpoint/dashboard"
                    element={<Navigate to="/perkpoint" replace />}
                  />
                  <Route
                    path="/perkpoint/programs"
                    element={
                      <Navigate to="/perkpoint/program/setup" replace />
                    }
                  />
                  <Route
                    path="/perkpoint/create-program"
                    element={
                      <Navigate to="/perkpoint/program/setup/create" replace />
                    }
                  />
                  <Route
                    path="/perkpoint/programs/create"
                    element={
                      <Navigate to="/perkpoint/program/setup/create" replace />
                    }
                  />
                  <Route
                    path="/perkpoint/program/overview"
                    element={<Navigate to="/perkpoint" replace />}
                  />
                  <Route
                    path="/perkpoint/program/setup/create"
                    element={<PerkPointCreateProgram />}
                  />
                  <Route
                    path="/perkpoint/program/setup/:id"
                    element={<PerkPointProgramDetails />}
                  />
                  <Route
                    path="/perkpoint/program/setup"
                    element={<PerkPointProgramSetup />}
                  />
                  <Route
                    path="/perkpoint/program/:id"
                    element={<PerkPointProgramDetails />}
                  />
                  <Route
                    path="/perkpoint/member/all"
                    element={<PerkPointAllMembers />}
                  />
                  <Route
                    path="/perkpoint/member/history"
                    element={<PerkPointMemberHistory />}
                  />
                  <Route
                    path="/perkpoint/member/segments"
                    element={<PerkPointMemberSegments />}
                  />
                  <Route
                    path="/perkpoint/member/insight"
                    element={<PerkPointMemberInsight />}
                  />
                  <Route
                    path="/perkpoint/member/ai-recommendations"
                    element={
                      <Navigate to="/perkpoint/member/insight" replace />
                    }
                  />
                  <Route
                    path="/perkpoint/rewards/rules"
                    element={<RewardsRules />}
                  />
                  <Route
                    path="/perkpoint/rewards/tiers"
                    element={<RewardsTiers />}
                  />
                  <Route
                    path="/perkpoint/rewards/campaigns"
                    element={<RewardsCampaigns />}
                  />
                  <Route
                    path="/perkpoint/rewards/campaigns/create"
                    element={<CampaignBuilder />}
                  />
                  <Route
                    path="/perkpoint/rewards/campaigns/:id/edit"
                    element={<CampaignBuilder />}
                  />
                  <Route
                    path="/perkpoint/rewards/campaigns/:id/performance"
                    element={<CampaignPerformance />}
                  />
                  <Route
                    path="/perkpoint/rewards/campaigns/:id"
                    element={<CampaignDetails />}
                  />
                  <Route
                    path="/perkpoint/rewards/coupons"
                    element={<RewardsCoupons />}
                  />
                  <Route
                    path="/perkpoint/rewards/gamification"
                    element={<RewardsGamification />}
                  />
                  <Route
                    path="/perkpoint/audit/history"
                    element={<AuditHistory />}
                  />
                  <Route
                    path="/perkpoint/members"
                    element={<PerkPointMembers />}
                  />
                  <Route
                    path="/perkpoint/create-member"
                    element={<PerkPointCreateMember />}
                  />
                  <Route
                    path="/perkpoint/member/:id"
                    element={<PerkPointMemberDetails />}
                  />
                  <Route
                    path="/perkpoint/transactions"
                    element={<PerkPointTransactions />}
                  />
                  <Route
                    path="/perkpoint/analytics"
                    element={<PerkPointAnalytics />}
                  />
                  <Route
                    path="/perkpoint/rule-engine"
                    element={<RuleEngine />}
                  />
                  <Route path="/perkpoint/coupons" element={<Coupons />} />
                  <Route path="/perkpoint/tier" element={<Tier />} />
                  <Route
                    path="/perkpoint/transaction"
                    element={<InsertTransaction />}
                  />
                  <Route path="/perkpoint/campaign" element={<Campaign />} />
                  <Route
                    path="/perkpoint/gamification"
                    element={<Gamification />}
                  />
                  <Route
                    path="/gamepoint"
                    element={<Navigate to="/gamepoint/analytics" replace />}
                  />
                  <Route
                    path="/gamepoint/gamification"
                    element={<Navigate to="/gamepoint/analytics" replace />}
                  />
                  <Route
                    path="/gamepoint/analytics"
                    element={<GamePointGamification />}
                  />
                  <Route
                    path="/gamepoint/spin-a-wheel"
                    element={<GamePointGamification />}
                  />
                  <Route
                    path="/gamepoint/scratch-card"
                    element={<GamePointGamification />}
                  />
                  <Route
                    path="/gamepoint/slot-machine"
                    element={<GamePointGamification />}
                  />
                  <Route
                    path="/gamepoint/mystery-box"
                    element={<GamePointGamification />}
                  />
                  <Route
                    path="/gamepoint/quiz"
                    element={<GamePointGamification />}
                  />
                  <Route
                    path="/gamepoint/report"
                    element={<GamePointGamification />}
                  />
                  <Route path="/perkpoint/report" element={<Report />} />
                  <Route
                    path="/perkpoint/communication"
                    element={<Communication />}
                  />
                  <Route path="/perkpoint/member-360" element={<Member360 />} />
                  <Route
                    path="/perkpoint/user-roles-management"
                    element={<UserRolesManagement />}
                  />
                  <Route
                    path="/perkpoint/segmentation"
                    element={<Segmentation />}
                  />
                  <Route path="/perkpoint/outlets" element={<Outlets />} />
                  <Route
                    path="/perkpoint/ai-assistance"
                    element={<AIAssistance />}
                  />
                  <Route path="/perkpoint/ai-chat" element={<AIChat />} />
                  <Route
                    path="/perkpoint/fraud-detection"
                    element={<FraudDetection />}
                  />
                  <Route
                    path="/perkpoint/ai-recommendations"
                    element={<AIRecommendations />}
                  />

                  {/* Reward Points Routes */}
                  <Route
                    path="/rewardpoint"
                    element={<RewardPointPrograms />}
                  />
                  <Route
                    path="/rewardpoint/programs"
                    element={<RewardPointPrograms />}
                  />
                  <Route
                    path="/rewardpoint/programs/create"
                    element={<CreateRewardPointProgram />}
                  />
                  <Route
                    path="/rewardpoint/programs/:id"
                    element={<RewardProgramDetails />}
                  />
                  <Route
                    path="/rewardpoint/categories"
                    element={<PlatformCategories />}
                  />
                  <Route
                    path="/rewardpoint/program-categories"
                    element={<ProgramCategories />}
                  />
                  <Route
                    path="/rewardpoint/brands"
                    element={<RewardPointBrands />}
                  />
                  <Route
                    path="/rewardpoint/products"
                    element={<ProductsLayout />}
                  >
                    <Route index element={<ProductsPage />} />
                    <Route path="merchandise" element={<MerchandisePage />} />
                    <Route
                      path="flight-booking"
                      element={<FlightBookingPage />}
                    />
                    <Route
                      path="hotel-booking"
                      element={<HotelBookingPage />}
                    />
                    <Route
                      path="gift-cards/create"
                      element={<GiftCardProductCreatePage />}
                    />
                    <Route
                      path="merchandise/create"
                      element={<MerchandiseProductCreatePage />}
                    />
                    <Route
                      path="flight-booking/create"
                      element={<FlightBookingProductCreatePage />}
                    />
                    <Route
                      path="hotel-booking/create"
                      element={<HotelBookingProductCreatePage />}
                    />
                    <Route
                      path="gift-card-view/:id"
                      element={<GiftCardProductView />}
                    />
                    <Route
                      path="merchandise-view/:id"
                      element={<MerchandiseProductView />}
                    />
                    <Route
                      path="flight-booking-view/:id"
                      element={<FlightBookingProductView />}
                    />
                    <Route
                      path="hotel-booking-view/:id"
                      element={<HotelBookingProductView />}
                    />
                  </Route>
                  <Route
                    path="/rewardpoint/orders"
                    element={<RewardPointOrders />}
                  />
                  <Route
                    path="/rewardpoint/orders/:id"
                    element={<OrderDetails />}
                  />
                  <Route
                    path="/rewardpoint/invoices"
                    element={<RewardPointInvoices />}
                  />
                  <Route
                    path="/rewardpoint/redemption-merchants"
                    element={<RedemptionMerchants />}
                  />
                  <Route
                    path="/rewardpoint/redemption-merchants/create"
                    element={<RedemptionMerchantCreate />}
                  />
                  <Route
                    path="/rewardpoint/redemption-merchants/view/:id"
                    element={<RedemptionMerchantView />}
                  />
                  <Route
                    path="/rewardpoint/shipping-profiles"
                    element={<ShippingProfiles />}
                  />
                  <Route
                    path="/rewardpoint/shipping-profiles/create"
                    element={<CreateShippingProfile />}
                  />
                  <Route
                    path="/rewardpoint/shipping-profiles/:id"
                    element={<ShippingProfileDetails />}
                  />

                  <Route path="/rewardpoint/jobs" element={<Jobs />} />
                  <Route path="/rewardpoint/users" element={<Users />} />
                  <Route path="/rewardpoint/settings" element={<Settings />} />
                  <Route path="/rewardpoint/clients" element={<Clients />} />
                  <Route
                    path="/rewardpoint/clients/create"
                    element={<CreateClient />}
                  />
                  <Route
                    path="/rewardpoint/clients/:id"
                    element={<ClientDetails />}
                  />

                  {/* Xpand Point Routes */}
                  <Route path="/xpandpoint" element={<XpandPointDashboard />} />
                  <Route
                    path="/xpandpoint/dashboard"
                    element={<Navigate to="/xpandpoint" replace />}
                  />
                  <Route
                    path="/xpandpoint/programs"
                    element={<XpandPointPrograms />}
                    // for now its transfer partner
                  />
                  <Route
                    path="/xpandpoint/partners"
                    element={<XpandPointPrograms />}
                    // for now its transfer partner
                  />
                  <Route
                    path="/xpandpoint/programs/create"
                    element={<XpandPointCreateProgram />}
                    // for now its create transfer partner
                  />
                  <Route
                    path="/xpandpoint/partner/create"
                    element={<XpandPointCreateProgram />}
                    // for now its create transfer partner
                  />
                  <Route
                    path="/xpandpoint/programs/:id"
                    element={<XpandPointProgramDetails />}
                    // partner details
                  />
                  <Route
                    path="/xpandpoint/partners/:id"
                    element={<XpandPointProgramDetails />}
                    // partner details
                  />
                  <Route
                    path="/xpandpoint/transfer-partners"
                    element={<TransferPrograms />}
                  />
                  <Route
                    path="/xpandpoint/transfer-programs"
                    element={<TransferPrograms />}
                  />
                  <Route
                    path="/xpandpoint/transfer-partners/:id"
                    element={<TransferPartnerDetails />}
                  />
                  <Route
                    path="/xpandpoint/transfer-programs/:id"
                    element={<TransferPartnerDetails />}
                  />
                  <Route
                    path="/xpandpoint/transfer-partners/create"
                    element={<TransferPartnerCreate />}
                  />
                  <Route
                    path="/xpandpoint/transfer-programs/create"
                    element={<TransferPartnerCreate />}
                  />
                  <Route
                    path="/xpandpoint/transactions"
                    element={<XpandPointTransactions />}
                  />
                  <Route
                    path="/xpandpoint/accounting-entry"
                    element={<XpandPointAccountingEntry />}
                  />
                  <Route
                    path="/xpandpoint/invoices"
                    element={<XpandPointInvoices />}
                  />
                  <Route path="/xpandpoint/analytics" element={<Analytics />} />
                  <Route path="/xpandpoint/jobs" element={<Jobs />} />
                  <Route path="/xpandpoint/users" element={<Users />} />
                  <Route path="/xpandpoint/settings" element={<Settings />} />
                  <Route path="/xpandpoint/clients" element={<Clients />} />
                  <Route
                    path="/xpandpoint/clients/create"
                    element={<CreateClient />}
                  />
                  <Route
                    path="/xpandpoint/clients/:id"
                    element={<ClientDetails />}
                  />

                  {/* Gift Card Routes */}
                  <Route path="/giftcard" element={<GiftCardDashboard />} />
                  <Route
                    path="/giftcard/allissuedgiftcards"
                    element={<AllGiftCards />}
                  />
                  <Route path="/giftcard/issue" element={<IssueGiftCard />} />
                  <Route path="/giftcard/redeem" element={<RedeemGiftCard />} />
                  <Route
                    path="/giftcard/campaign"
                    element={<GiftCardCampaign />}
                  />
                  <Route path="/giftcard/logs" element={<GiftCardLogs />} />
                  <Route
                    path="/giftcard/all-giftcards"
                    element={<CreateGiftCardList />}
                  />
                  <Route
                    path="/giftcard/create-new"
                    element={<CreateNewGiftCard />}
                  />
                  <Route path="/giftcard/brands" element={<GiftCardBrands />} />
                  <Route
                    path="/giftcard/brands/create"
                    element={<CreateGiftCardBrand />}
                  />
                  <Route path="/giftcard/mall" element={<GiftCardMall />} />
                  <Route
                    path="/giftcard/settings"
                    element={<GiftCardSettings />}
                  />

                  {/* Shared Routes (Users and Settings) */}
                  <Route path="/users" element={<Users />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route
                    path="/rewardpoint/analytics-new"
                    element={<Analytics />}
                  />
                  <Route path="*" element={<NotFound />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </div>
        </TooltipProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
